import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);

const load = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};

export function StoreProvider({ children }) {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState(() => load("jn-cart", []));
  const [wishlist, setWishlist] = useState(() => load("jn-wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => { localStorage.setItem("jn-cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("jn-wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const addToCart = useCallback((product, size, qty = 1, pieceSizes = null) => {
    const key = pieceSizes
      ? `${product.slug}:${Object.keys(pieceSizes).sort().map((k) => `${k}=${pieceSizes[k]}`).join("|")}`
      : `${product.slug}:${size}`;
    setCart((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => i.key === key ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { key, slug: product.slug, name: product.name, price: product.price, image: product.images[0], size, qty, piece_sizes: pieceSizes,
        piece_labels: product.pieces ? Object.fromEntries(product.pieces.map((p) => [p.slug, p.label])) : null }];
    });
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((key, delta) => {
    setCart((prev) => prev.map((i) => i.key === key ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const has = prev.includes(product.slug);
      toast(has ? "Removed from wishlist" : "Saved to wishlist", { description: product.name });
      return has ? prev.filter((s) => s !== product.slug) : [...prev, product.slug];
    });
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  }, []);

  const cartCount = cart.reduce((n, i) => n + i.qty, 0);
  const cartTotal = cart.reduce((n, i) => n + i.qty * i.price, 0);

  return (
    <StoreContext.Provider value={{
      user, setUser, cart, cartCount, cartTotal, cartOpen, setCartOpen,
      addToCart, updateQty, removeFromCart, clearCart, wishlist, toggleWishlist, logout,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
